/* eslint-disable */
import { useState } from "react";
import { notifyEnquiry } from "../email";
import { B, store, Btn, SecLabel, getTracking } from "../shared";

// ─── CALCULATIONS ──────────────────────────────────────────────────────────────
// Amortising loan payment: P * r(1+r)^n / ((1+r)^n - 1)
function amortPayment(principal, annualRatePct, years) {
  const r = annualRatePct / 12 / 100;
  const n = years * 12;
  if (r === 0) return principal / n;
  const pow = Math.pow(1 + r, n);
  return (principal * r * pow) / (pow - 1);
}

// Amortising payment with a balloon/residual settled at term end:
// (PV - FV/(1+r)^n) * r / (1 - (1+r)^-n)
function amortWithBalloon(principal, balloon, annualRatePct, years) {
  const r = annualRatePct / 12 / 100;
  const n = years * 12;
  if (r === 0) return (principal - balloon) / n;
  const pow = Math.pow(1 + r, n);
  return ((principal - balloon / pow) * r) / (1 - 1 / pow);
}

// Present value of an annuity (max loan supportable by a fixed monthly payment)
function annuityPV(monthlyPayment, annualRatePct, years) {
  const r = annualRatePct / 12 / 100;
  const n = years * 12;
  if (r === 0) return monthlyPayment * n;
  return (monthlyPayment * (1 - Math.pow(1 + r, -n))) / r;
}

const gbp = (n) => `£${Math.round(n).toLocaleString("en-GB")}`;

const inputStyle = { width: "100%", accentColor: B.blue };
const fieldLabel = { fontSize: 9, color: B.inkM, display: "flex", justifyContent: "space-between", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 };
const fieldWrap = { marginBottom: 18 };

function Field({ label, value, valueLabel, min, max, step, onChange, suffix }) {
  return (
    <div style={fieldWrap}>
      <label style={fieldLabel}>
        <span>{label}</span>
        <span style={{ color: B.navy, fontWeight: 700 }}>{valueLabel ?? value}{suffix || ""}</span>
      </label>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} style={inputStyle} />
    </div>
  );
}

function ResultCard({ title, rows, note }) {
  return (
    <div style={{ background: `linear-gradient(135deg,${B.navy},${B.navyM})`, borderRadius: 14, padding: "24px 26px" }}>
      <p className="mono" style={{ color: "rgba(255,255,255,0.4)", marginBottom: 16 }}>{title}</p>
      {rows.map(([label, val, big], i) => (
        <div key={i} style={{ marginBottom: i < rows.length - 1 ? 14 : 0 }}>
          <p style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", marginBottom: 2 }}>{label}</p>
          <p className="jk" style={{ fontSize: big ? 30 : 16, fontWeight: 700, color: "white" }}>{val}</p>
        </div>
      ))}
      {note && <p style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", lineHeight: 1.6, marginTop: 16 }}>{note}</p>}
    </div>
  );
}

const TABS = [
  { id: "loan", label: "Business Loan" },
  { id: "asset", label: "Asset & Equipment Finance" },
  { id: "invoice", label: "Invoice Finance" },
  { id: "borrowing", label: "Borrowing Power" },
];

function Calculator({ setPage }) {
  const [tab, setTab] = useState("loan");

  // Business Loan
  const [loanAmount, setLoanAmount] = useState(50000);
  const [loanTerm, setLoanTerm] = useState(3);
  const [tradingTime, setTradingTime] = useState("3+");
  const loanRate = tradingTime === "<1" ? 15.9 : tradingTime === "1-3" ? 11.9 : 8.9;
  const loanMonthly = amortPayment(loanAmount, loanRate, loanTerm);

  // Asset & Equipment Finance
  const [assetCost, setAssetCost] = useState(50000);
  const [deposit, setDeposit] = useState(0);
  const [assetTerm, setAssetTerm] = useState(3);
  const [balloon, setBalloon] = useState(0);
  const assetPrincipal = Math.max(assetCost - deposit, 0);
  const assetMonthly = amortWithBalloon(assetPrincipal, Math.min(balloon, assetPrincipal), 9.5, assetTerm);

  // Invoice Finance
  const [ledger, setLedger] = useState(100000);
  const [advanceRate, setAdvanceRate] = useState(85);
  const [serviceFee, setServiceFee] = useState(0.75);
  const [discountRate, setDiscountRate] = useState(2);
  const invFunding = ledger * (advanceRate / 100);
  const invMonthlyCost = ledger * (serviceFee / 100) + invFunding * (discountRate / 100);

  // Borrowing Power
  const [monthlyProfit, setMonthlyProfit] = useState(10000);
  const [existingDebt, setExistingDebt] = useState(1000);
  const [borrowTerm, setBorrowTerm] = useState(5);
  const affordability = Math.max(monthlyProfit * 0.3 - existingDebt, 0);
  const maxLoan = annuityPV(affordability, 9.9, borrowTerm);

  // Lead capture
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", website: "" });
  const [sent, setSent] = useState(false);
  const [saving, setSaving] = useState(false);
  const s = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const valid = form.name && form.company && form.email && form.phone;

  const summaryForTab = () => {
    if (tab === "loan") return `Business Loan: ${gbp(loanAmount)} over ${loanTerm}yr, trading ${tradingTime}yr → indicative ${gbp(loanMonthly)}/mo`;
    if (tab === "asset") return `Asset Finance: ${gbp(assetCost)} cost, ${gbp(deposit)} deposit, ${assetTerm}yr, ${gbp(balloon)} balloon → indicative ${gbp(assetMonthly)}/mo`;
    if (tab === "invoice") return `Invoice Finance: ${gbp(ledger)} ledger, ${advanceRate}% advance → ${gbp(invFunding)} funding, ${gbp(invMonthlyCost)}/mo cost`;
    return `Borrowing Power: ${gbp(monthlyProfit)}/mo profit, ${gbp(existingDebt)}/mo existing debt, ${borrowTerm}yr → indicative max loan ${gbp(maxLoan)}`;
  };

  const submit = async () => {
    if (form.website) { setSent(true); return; } // honeypot tripped = bot; pretend success, save nothing
    setSaving(true);
    const enquiryForm = { name: form.name, email: form.email, phone: form.phone, business: form.company, service: "Finance Calculator", message: summaryForTab() };
    await store.save(`enquiry:${Date.now()}`, { type: "enquiry", ...enquiryForm, tracking: getTracking(), source: "calculator" });
    await notifyEnquiry(enquiryForm);
    setSaving(false);
    setSent(true);
  };

  return (
    <>
      {/* HERO — task tool, not a marketing page: no orbs, no useReveal() */}
      <section style={{ background: `linear-gradient(135deg,${B.navy},${B.navyM})`, padding: "48px 48px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 14 }}>
            <div style={{ fontSize: 40 }}>🧮</div>
            <h1 className="jk hero-h1" style={{ fontWeight: 800, letterSpacing: "-0.03em", color: "white" }}>Finance Calculator</h1>
          </div>
          <p style={{ fontSize: 15, color: "rgba(255,255,255,0.75)", fontWeight: 300, lineHeight: 1.7, maxWidth: 560 }}>
            Get an indicative figure for a business loan, asset finance, invoice finance or your borrowing power — in under a minute. No obligation, no credit check.
          </p>
        </div>
      </section>

      {/* TABS */}
      <section style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 48px 0" }}>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", background: "white", border: `1px solid ${B.border}`, borderRadius: 10, padding: 4, width: "fit-content" }}>
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: "8px 16px", borderRadius: 7, fontSize: 11, fontWeight: 700, background: tab === t.id ? B.navy : "transparent", color: tab === t.id ? "white" : B.inkM, border: "none", cursor: "pointer" }}>
              {t.label}
            </button>
          ))}
        </div>
      </section>

      {/* CALCULATOR BODY */}
      <section style={{ maxWidth: 1100, margin: "0 auto", padding: "28px 48px 56px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: 32, alignItems: "start" }}>
          <div style={{ background: "white", border: `1px solid ${B.border}`, borderRadius: 14, padding: "28px 30px" }}>
            {tab === "loan" && (
              <>
                <Field label="Loan amount" value={loanAmount} valueLabel={gbp(loanAmount)} min={10000} max={2000000} step={5000} onChange={setLoanAmount} />
                <Field label="Term" value={loanTerm} valueLabel={`${loanTerm} yr${loanTerm > 1 ? "s" : ""}`} min={1} max={7} step={1} onChange={setLoanTerm} />
                <div style={fieldWrap}>
                  <label style={fieldLabel}><span>Time trading</span></label>
                  <div style={{ display: "flex", gap: 6 }}>
                    {[["<1", "Under 1 year"], ["1-3", "1–3 years"], ["3+", "3+ years"]].map(([id, l]) => (
                      <button key={id} onClick={() => setTradingTime(id)} style={{ flex: 1, padding: "8px 10px", borderRadius: 8, fontSize: 11, fontWeight: 600, background: tradingTime === id ? B.blueFaint : "white", border: `1.5px solid ${tradingTime === id ? B.blue : B.border}`, color: tradingTime === id ? B.blue : B.inkS, cursor: "pointer" }}>{l}</button>
                    ))}
                  </div>
                </div>
              </>
            )}
            {tab === "asset" && (
              <>
                <Field label="Asset / equipment cost" value={assetCost} valueLabel={gbp(assetCost)} min={10000} max={2000000} step={5000} onChange={setAssetCost} />
                <Field label="Deposit" value={deposit} valueLabel={gbp(deposit)} min={0} max={Math.round(assetCost * 0.5)} step={1000} onChange={setDeposit} />
                <Field label="Term" value={assetTerm} valueLabel={`${assetTerm} yr${assetTerm > 1 ? "s" : ""}`} min={1} max={7} step={1} onChange={setAssetTerm} />
                <Field label="Balloon / residual (optional)" value={balloon} valueLabel={gbp(balloon)} min={0} max={Math.round(assetCost * 0.5)} step={1000} onChange={setBalloon} />
              </>
            )}
            {tab === "invoice" && (
              <>
                <Field label="Monthly sales ledger" value={ledger} valueLabel={gbp(ledger)} min={10000} max={1000000} step={5000} onChange={setLedger} />
                <Field label="Advance rate" value={advanceRate} suffix="%" min={70} max={90} step={1} onChange={setAdvanceRate} />
                <Field label="Service fee" value={serviceFee} suffix="%" min={0.2} max={3} step={0.05} onChange={setServiceFee} />
                <Field label="Discount rate (per month)" value={discountRate} suffix="%" min={1} max={3} step={0.1} onChange={setDiscountRate} />
              </>
            )}
            {tab === "borrowing" && (
              <>
                <Field label="Average monthly net profit" value={monthlyProfit} valueLabel={gbp(monthlyProfit)} min={1000} max={100000} step={500} onChange={setMonthlyProfit} />
                <Field label="Existing monthly debt repayments" value={existingDebt} valueLabel={gbp(existingDebt)} min={0} max={20000} step={250} onChange={setExistingDebt} />
                <Field label="Term" value={borrowTerm} valueLabel={`${borrowTerm} yr${borrowTerm > 1 ? "s" : ""}`} min={1} max={10} step={1} onChange={setBorrowTerm} />
              </>
            )}
          </div>

          {tab === "loan" && <ResultCard title="Indicative Figures" rows={[["Monthly payment", `${gbp(loanMonthly)}/mo`, true], ["Total repayable", gbp(loanMonthly * loanTerm * 12)], ["Total interest", gbp(loanMonthly * loanTerm * 12 - loanAmount)], ["Representative rate", `${loanRate}% p.a.`]]} note="Rate band is illustrative and based on time trading. Your actual rate depends on full underwriting." />}
          {tab === "asset" && <ResultCard title="Indicative Figures" rows={[["Monthly payment", `${gbp(assetMonthly)}/mo`, true], ["Amount financed", gbp(assetPrincipal)], ["Total payable", gbp(assetMonthly * assetTerm * 12 + Math.min(balloon, assetPrincipal))], ["Representative rate", "9.5% p.a."]]} note="Balloon/residual is settled as a lump sum at the end of the term." />}
          {tab === "invoice" && <ResultCard title="Indicative Figures" rows={[["Funding released", gbp(invFunding), true], ["Estimated monthly cost", `${gbp(invMonthlyCost)}/mo`], ["Advance rate", `${advanceRate}%`]]} note="Funding released is based on your sales ledger value, typically available within 24–48 hours of invoicing." />}
          {tab === "borrowing" && <ResultCard title="Indicative Figures" rows={[["Estimated max loan", gbp(maxLoan), true], ["Affordable monthly repayment", `${gbp(affordability)}/mo`], ["Representative rate", "9.9% p.a."]]} note="Affordability is estimated at 30% of monthly net profit, less existing debt commitments." />}
        </div>
      </section>

      {/* LEAD CAPTURE */}
      <section style={{ maxWidth: 1100, margin: "0 auto", padding: "0 48px 64px" }}>
        {sent ? (
          <div style={{ background: "white", border: `1px solid ${B.border}`, borderRadius: 14, padding: "32px", textAlign: "center" }}>
            <h3 className="jk" style={{ fontSize: 18, fontWeight: 700, color: B.navy, marginBottom: 8 }}>Thanks — we'll be in touch shortly.</h3>
            <p style={{ fontSize: 12, color: B.inkM, fontWeight: 300 }}>A member of our team will follow up on your calculation within one business day.</p>
          </div>
        ) : (
          <div style={{ background: "white", border: `1px solid ${B.border}`, borderRadius: 14, padding: "28px 30px" }}>
            <SecLabel>Get This As A Personalised Quote</SecLabel>
            <h2 className="jk section-h2" style={{ fontWeight: 700, letterSpacing: "-0.02em", color: B.navy, marginBottom: 18 }}>Turn this estimate into a real conversation.</h2>
            <input value={form.website} onChange={(e) => s("website", e.target.value)} tabIndex={-1} autoComplete="off" placeholder="Website" style={{ position: "absolute", left: "-9999px", height: 1, width: 1, opacity: 0 }} aria-hidden="true" />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              {[["Full name", "name", "text"], ["Company", "company", "text"], ["Email", "email", "email"], ["Phone", "phone", "tel"]].map(([l, k, t]) => (
                <div key={k}>
                  <label style={{ fontSize: 9, color: B.inkM, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>{l}</label>
                  <input value={form[k]} onChange={(e) => s(k, e.target.value)} type={t} style={{ width: "100%", background: B.off, border: `1.5px solid ${B.border}`, borderRadius: 7, padding: "9px 11px", fontSize: 12, color: B.navy, outline: "none" }} onFocus={(e) => (e.target.style.borderColor = B.blue)} onBlur={(e) => (e.target.style.borderColor = B.border)} />
                </div>
              ))}
            </div>
            <Btn style={{ borderRadius: 8 }} disabled={!valid || saving} onClick={submit}>{saving ? "Sending…" : "Get My Personalised Quote →"}</Btn>
          </div>
        )}
      </section>

      {/* COMPLIANCE FOOTER */}
      <section style={{ maxWidth: 1100, margin: "0 auto", padding: "0 48px 56px" }}>
        <div style={{ background: B.light, border: `1px solid ${B.border}`, borderRadius: 8, padding: "12px 16px" }}>
          <p style={{ fontSize: 9, color: B.inkM, lineHeight: 1.7, fontWeight: 300 }}>
            Figures shown are indicative only, calculated from the information you enter and representative rates — they do not constitute a loan offer, quote or guarantee of funding. CapitalOS operates as a credit broker, not a lender, and may receive commission from lenders for successful introductions. All funding is subject to status, eligibility, affordability and lender criteria.
          </p>
        </div>
      </section>
    </>
  );
}

export { Calculator };
